import type { ReactElement } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface InvoiceDropZoneProps {
  onFiles: (files: File[]) => void
  disabled?: boolean
  className?: string
  idleText?: string
  dragActiveText?: string
}

export function InvoiceDropZone({
  onFiles,
  disabled,
  className,
  idleText = 'Drop invoice(s) or click to upload',
  dragActiveText = 'Drop file(s) here...',
}: InvoiceDropZoneProps): ReactElement {
  const handleDrop = (accepted: File[], rejected: FileRejection[]): void => {
    if (rejected.length > 0) {
      const msg = rejected[0]?.errors?.[0]?.message ?? 'Invalid file'
      toast.error(msg)
    }
    if (accepted.length > 0) {
      onFiles(accepted)
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: true,
    disabled: disabled ?? false,
  })

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200',
        'flex items-center justify-center gap-2 text-sm text-muted-foreground',
        isDragActive
          ? 'h-20 border-primary/60 bg-accent/30 text-foreground'
          : 'h-12 border-border hover:border-primary/40',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <input {...getInputProps()} />
      <Upload className="h-4 w-4" />
      <span>{isDragActive ? dragActiveText : idleText}</span>
    </div>
  )
}
